import React, { useCallback, useEffect, useState } from "react";
import ReactDom from "react-dom";

import styles from "./delinquencyPredictedModal.module.css";
import Button from "../../button/button";
import { delinquencyPredictedStudents } from "../../../services/prediction";
import { addPorcentage, formatNumberToCurrency, formatShortDate } from "../../../utils/formatter";
import { downloadAsCsv, downloadAsExcel } from "../../../utils/tableExport";

import loadingGif from "../../../assets/loading.gif";
import DownloadIcon from "../../../assets/icons/download.svg";

// "atencao": faixa de cobertura (20% de maior risco do lote) abaixo de médio/alto
const RISK_LABEL = { alto: "Alto", medio: "Médio", atencao: "Atenção", baixo: "Baixo" };

const round2 = (value) => Math.round(Number(value || 0) * 100) / 100;

// "set/2026" a partir do RefMonth (DATE vindo como ISO).
const formatRefMonth = (value) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("pt-BR", { month: "short", year: "numeric" }).replace(".", "");
};

const riskClass = (band) => {
  if (band === "alto") {
    return styles.riskHigh;
  }
  if (band === "medio") {
    return styles.riskMedium;
  }
  return styles.riskLow;
};

// Colunas do arquivo baixado: as da tabela mais o que explica a pontuação. O valor
// estimado por aluno é o ticket médio, igual ao cálculo do card (n × ticket médio).
export const toExportRows = (students, averageTicket) =>
  students.map((s) => ({
    "Id do Aluno": s.studentId,
    Curso: s.courseName || "",
    "Probabilidade (%)": round2(s.probability * 100),
    "Limiar do modelo (%)": round2(s.threshold * 100),
    "Faixa de risco": RISK_LABEL[s.riskBand] || s.riskBand || "",
    "Parcelas vencidas": s.overdueInstallments ?? 0,
    "Valor vencido (R$)": round2(s.overdueAmount),
    "Valor estimado em risco (R$)": round2(averageTicket),
    "Mês de referência": formatRefMonth(s.refMonth),
    "Horizonte (dias)": s.horizonDays ?? "",
    "Pontuado em": formatShortDate(s.scoredAt)
  }));

const FILE_BASE = "alunos-risco-inadimplencia";

// Drill-down dos números do card "Predição · Inadimplência" da home: quem são os
// alunos por trás de "alunos com risco previsto" e de onde sai o valor em risco.
const DelinquencyPredictedModal = ({ institutionId, averageTicket = 0, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [students, setStudents] = useState([]);
  const [lastScoredAt, setLastScoredAt] = useState(null);

  const load = useCallback(async () => {
    if (!institutionId) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await delinquencyPredictedStudents({ institutionId });
      setStudents(Array.isArray(data?.students) ? data.students : []);
      setLastScoredAt(data?.lastScoredAt || null);
    } catch (err) {
      setError(err?.message || "Não foi possível carregar os alunos.");
    } finally {
      setLoading(false);
    }
  }, [institutionId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const amountAtRisk = students.length * (averageTicket || 0);
  const exportRows = () => toExportRows(students, averageTicket);

  const renderBody = () => {
    if (loading) {
      return (
        <div className={styles.state}>
          <img src={loadingGif} alt="Carregando..." />
        </div>
      );
    }

    if (error) {
      return (
        <div className={styles.state}>
          <span className={styles.stateText}>{error}</span>
          <Button text="Tentar novamente" variation="outline" size="normal" onClick={load} />
        </div>
      );
    }

    if (!students.length) {
      return (
        <div className={styles.state}>
          <span className={styles.stateText}>Nenhum aluno acima do limiar do modelo nesta pontuação.</span>
        </div>
      );
    }

    return (
      <div className={styles.tableWrapper}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Aluno</th>
              <th>Curso</th>
              <th className={styles.numeric}>Probabilidade</th>
              <th className={styles.numeric}>Parcelas vencidas</th>
              <th className={styles.numeric}>Valor estimado</th>
            </tr>
          </thead>
          <tbody>
            {students.map((s) => (
              <tr key={s.studentId}>
                <td className={styles.studentId}>{s.studentId}</td>
                <td>{s.courseName || "-"}</td>
                <td className={styles.numeric}>
                  <span className={`${styles.risk} ${riskClass(s.riskBand)}`}>
                    {addPorcentage(s.probability * 100)}
                  </span>
                </td>
                <td className={styles.numeric}>
                  {s.overdueInstallments > 0
                    ? `${s.overdueInstallments} · ${formatNumberToCurrency(s.overdueAmount)}`
                    : "nenhuma"}
                </td>
                <td className={`${styles.numeric} ${styles.value}`}>{formatNumberToCurrency(averageTicket)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return ReactDom.createPortal(
    <div className={styles.modalBackdrop} onClick={onClose}>
      <div
        className={styles.modalContent}
        role="dialog"
        aria-modal="true"
        aria-labelledby="delinquency-predicted-title"
        onClick={(event) => event.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <div>
            <span className={styles.eyebrow}>PREDIÇÃO · INADIMPLÊNCIA</span>
            <h3 id="delinquency-predicted-title">Alunos com risco previsto</h3>
            <span className={styles.subtitle}>
              Previsão para os próximos 30 dias
              {lastScoredAt ? ` · pontuação de ${formatShortDate(lastScoredAt)}` : ""}
            </span>
          </div>
          <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Fechar">
            ×
          </button>
        </div>

        {!loading && !error && (
          <div className={styles.meta}>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Alunos</span>
              <span className={styles.metaValue}>{students.length}</span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Valor em risco estimado</span>
              <span className={`${styles.metaValue} ${styles.metaValueDanger}`}>
                {formatNumberToCurrency(amountAtRisk)}
              </span>
            </div>
            <div className={styles.metaItem}>
              <span className={styles.metaLabel}>Ticket médio (base do valor)</span>
              <span className={styles.metaValue}>{formatNumberToCurrency(averageTicket)}</span>
            </div>
          </div>
        )}

        <div className={styles.body}>{renderBody()}</div>

        <div className={styles.footer}>
          <p className={styles.note}>
            Entram na lista os alunos cuja probabilidade ficou acima do limiar do modelo na última pontuação.
            O valor por aluno é o ticket médio da instituição, o mesmo cálculo do card.
          </p>
          <div className={styles.actions}>
            <Button
              icon={DownloadIcon}
              text="CSV"
              variation="outline"
              size="normal"
              disabled={loading || !!error || !students.length}
              onClick={() => downloadAsCsv(exportRows(), `${FILE_BASE}.csv`)}
            />
            <Button
              icon={DownloadIcon}
              text="Excel"
              variation="outline"
              size="normal"
              disabled={loading || !!error || !students.length}
              onClick={() => downloadAsExcel(exportRows(), `${FILE_BASE}.xlsx`, "Alunos")}
            />
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default DelinquencyPredictedModal;
